import {
  Connect,
  ConnectCancelEvent,
  ConnectDoneEvent,
  ConnectErrorEvent,
  ConnectEventHandlers,
  ConnectOptions
} from 'connect-web-sdk';
import { useCallback, useMemo, useState } from 'react';
import { AxRoute } from 'src/features/DUP';
import { useRequest, useResource } from 'src/hooks/useResource';
import { ArgyleUser } from 'src/types/applicantExperienceApi';

export type BankLinking = ReturnType<typeof useBankLinking>;

interface ConnectURL {
  bankLinkingURL: string;
}

interface AccountData {
  id: string;
  name: string;
  accountNumberDisplay: string;
  institution: { name: string; branding: { icon: string } };
}

interface DisconnectAccountResponse {
  success: boolean;
  accountId: string;
  message: string;
}

export interface BankAccount {
  accountId: string;
  accountName: string;
  institutionName: string;
  logoUrl: string;
  accountNumberDisplay: string;
}

export const useBankLinking = (argyleUser?: ArgyleUser) => {
  const { makeRequest } = useRequest();

  const [isLoadingWidget, setIsLoadingWidget] = useState<boolean>(false);

  const [accountsData, { refresh, applyLocalUpdates }] = useResource<
    AccountData[] | { error: string }
  >(argyleUser ? `/${AxRoute.ARGYLE_V2}/bank/accounts` : '');
  const [isDisconnectingAccount, setIsDisconnectingAccount] = useState<boolean>(false);

  const accountsConnected = useMemo(() => {
    if (!accountsData || 'error' in accountsData || !accountsData.length) {
      return [];
    }

    return accountsData.map((account) => ({
      accountId: account.id,
      accountName: account.name,
      accountNumberDisplay: account.accountNumberDisplay,
      institutionName: account.institution.name,
      logoUrl: account.institution.branding.icon
    }));
  }, [accountsData]);

  const disconnectAccount = async (accountId: string) => {
    if (Array.isArray(accountsData) && accountsData.length > 0) {
      setIsDisconnectingAccount(true);
      const res = await makeRequest<DisconnectAccountResponse>({
        path: `/${AxRoute.ARGYLE_V2}/bank/accounts/${accountId}`,
        method: 'DELETE'
      });

      if (res?.success) {
        applyLocalUpdates(accountsData.filter((account) => account.id !== accountId) || []);
      }
      setIsDisconnectingAccount(false);
    }
  };

  const openWidget = useCallback(async () => {
    if (!argyleUser) return;

    setIsLoadingWidget(true);

    const connectURL = await makeRequest<ConnectURL>({
      path: `/${AxRoute.ARGYLE_V2}/bank/connect_url`
    });
    setIsLoadingWidget(false);

    Connect.destroy();
    if (connectURL?.bankLinkingURL) {
      const connectEventHandlers: ConnectEventHandlers = {
        onDone: (event: ConnectDoneEvent) => {
          refresh();
        },
        onCancel: (event: ConnectCancelEvent) => {
          return;
        },
        onError: (event: ConnectErrorEvent) => {
          return;
        }
      };

      const connectOptions: ConnectOptions = {
        overlay: 'rgba(199,201,199, 0.5)'
      };

      Connect.launch(connectURL.bankLinkingURL, connectEventHandlers, connectOptions);
    }
  }, [argyleUser, makeRequest, refresh]);

  return {
    openWidget,
    isLoadingWidget,
    isLoadingAccounts: argyleUser ? accountsData === undefined : false,
    isDisconnectingAccount,
    accountsConnected,
    getConnectedAccounts: refresh,
    disconnectAccount
  };
};
